/**
 * Everything Tilt Race sends to phones, as pure functions of plain values.
 * DOM-free and free of runtime imports so a test can load it under plain Node.
 */
import type { ControllerSpec } from '@forgesworn/joystick-host';

export const GAME_TITLE = 'TILT RACE';
export const SPEC_PREFIX = 'TILT';
export const MAX_PLAYERS = 4;
export const FALLBACK_COLOUR = '#b48cff';
/** Arcade initials: exactly three capital letters. */
export const INITIALS_RE = /^[A-Z]{3}$/;
export const PHASES = ['WAITING', 'STARTING', 'BETWEEN RACES', 'RACE LIVE'] as const;
export const PAUSE_MESSAGE = 'GAME PAUSED';
export const RESUME_MESSAGE = 'GAME RESUMED';
export const FINISH_LABELS = ['1st!', '2nd', '3rd', '4th'] as const;

export type RaceKind = 'lobby' | 'countdown' | 'racing' | 'over';
export interface LeaderRow { p: number; label: string; score: number; colour: string; tournamentTotal?: number }
export interface SpectatorViewData { title: string; leaderboard: LeaderRow[]; highlight?: string }

/** The name as stored from phone context: initials or nothing. */
export function acceptName(name: unknown): string | undefined {
  return typeof name === 'string' && INITIALS_RE.test(name) ? name : undefined;
}

export function marbleName(m: { p: number; ai: boolean }, name?: string): string {
  if (m.ai) return `P${m.p + 1} · BOT`;
  return name ? `P${m.p + 1} · ${name}` : `P${m.p + 1}`;
}

export function spectatorSpec(slot: number, colour: string): ControllerSpec {
  return {
    name: `${SPEC_PREFIX} · SPECTATOR ${slot - MAX_PLAYERS + 1}`,
    version: 1,
    slots: {
      A: { icon: '👏', label: 'CHEER', colour },
    },
  };
}

/** The addressed player spec: listing profile with the marble colour on the stick. */
export function playerSpec(base: ControllerSpec, p: number, colour: string): ControllerSpec {
  return {
    ...base,
    name: `${SPEC_PREFIX} · P${p + 1}`,
    slots: { ...base.slots, joyL: { ...base.slots.joyL, colour } },
  };
}

export function phaseFor(kind: string): (typeof PHASES)[number] {
  return kind === 'lobby' ? 'WAITING'
    : kind === 'countdown' ? 'STARTING'
    : kind === 'over' ? 'BETWEEN RACES'
    : 'RACE LIVE';
}

export function pauseMessage(paused: boolean): { type: 'controller-feedback'; message: string; locked: boolean } {
  return { type: 'controller-feedback', message: paused ? PAUSE_MESSAGE : RESUME_MESSAGE, locked: paused };
}

/** Label for a finishing position (0 = first). */
export function finishLabel(position: number): string {
  return position === 0 ? '1st!' : position === 1 ? '2nd' : position === 2 ? '3rd' : '4th';
}

export interface SpectatorInput {
  marbles: ReadonlyArray<{ p: number; ai: boolean; x: number; finishedAt: number | null }>;
  raceKind: string;
  names: ReadonlyMap<number, string>;
  tournamentTotals: ReadonlyMap<number, number>;
  /** Colour for a slot, as the host draws it. */
  colourFor: (p: number) => string;
  lastCheerSlot: number | null;
}

export function buildSpectatorView(input: SpectatorInput): SpectatorViewData {
  // Order: finished marbles first (in finish order), then by distance-to-finish.
  const marbles = [...input.marbles];
  marbles.sort((a, b) => {
    if (a.finishedAt !== null && b.finishedAt !== null) return a.finishedAt - b.finishedAt;
    if (a.finishedAt !== null) return -1;
    if (b.finishedAt !== null) return 1;
    return b.x - a.x;   // right = closer to finish
  });
  const nameOf = (m: { p: number; ai: boolean }): string => marbleName(m, input.names.get(m.p));
  return {
    title: `${GAME_TITLE} · ${phaseFor(input.raceKind)}`,
    leaderboard: marbles.slice(0, 4).map((m, ix) => ({
      p: m.p,
      label: m.finishedAt !== null ? `${ix + 1}. ${nameOf(m)}` : nameOf(m),
      score: m.finishedAt !== null ? ix + 1 : 0,
      colour: input.colourFor(m.p),
      ...(input.tournamentTotals.has(m.p) ? { tournamentTotal: input.tournamentTotals.get(m.p)! } : {}),
    })),
    ...(input.lastCheerSlot !== null
      ? { highlight: `${nameOf({ p: input.lastCheerSlot, ai: false })} cheered!` }
      : {}),
  };
}

const SAMPLE_COLOURS = ['#58ff58', '#ff8a3a', '#b48cff', '#ffd84a'];

/** Everything the game can emit, for a validator test. `base` is the listing profile. */
export function enumerateControllerStrings(base?: ControllerSpec): {
  specs: ControllerSpec[]; feedback: string[]; scoreLabels: string[];
  spectatorViews: { title: string; leaderboard: LeaderRow[]; highlight?: string }[];
} {
  const specs: ControllerSpec[] = [];
  const fallback: ControllerSpec = base ?? { name: SPEC_PREFIX, version: 1, slots: { joyL: {} } } as unknown as ControllerSpec;
  if (base) specs.push(base);
  for (let p = 0; p < MAX_PLAYERS; p++) specs.push(playerSpec(fallback, p, SAMPLE_COLOURS[p % 4]!));
  for (let slot = MAX_PLAYERS; slot < MAX_PLAYERS + 8; slot++) specs.push(spectatorSpec(slot, SAMPLE_COLOURS[slot % 4]!));
  const feedback = [PAUSE_MESSAGE, RESUME_MESSAGE];
  const scoreLabels = [0, 1, 2, 3].map(finishLabel);
  const spectatorViews: SpectatorViewData[] = [];
  const names = new Map<number, string>([[1, 'ABC'], [3, 'ZZZ'], [5, 'QRS']]);
  for (const raceKind of ['lobby', 'countdown', 'racing', 'over']) {
    for (const withTotals of [false, true]) {
      for (const cheer of [null, 2, 5, 11]) {
        for (const finished of [0, 2, 4]) {
          const marbles = [0, 1, 2, 3].map((p) => ({ p, ai: p % 2 === 0, x: 100 + p * 40, finishedAt: p < finished ? 10 + p : null }));
          const totals = new Map<number, number>(withTotals ? [[0, 0], [1, 200], [3, 35]] : []);
          spectatorViews.push(buildSpectatorView({ marbles, raceKind, names, tournamentTotals: totals, colourFor: (p) => SAMPLE_COLOURS[p % 4] ?? '#ffffff', lastCheerSlot: cheer }));
        }
      }
    }
  }
  return { specs, feedback, scoreLabels, spectatorViews };
}
